/**
 * The "approximate" / "not previewed" badge of the preview toolbar (W2-02; DESIGN.md D24, R13; CR-W2-01b-4): Phira's
 * built-in extra.json effects are reimplemented (W2-01b), not Phira's shaders, so the preview says so whenever one is
 * drawn; a built-in without a shader is drawn without its effect ("not previewed").
 */

import type { FrameDrawList } from '../../core/engine/types';
import type { BuiltinShader } from '../../core/model/types';
import { builtinShaderFidelity } from '../../render/preview/effects/builtin/index';

export interface BuiltinEffectsShown {
  readonly approximate: readonly BuiltinShader[];
  readonly notPreviewed: readonly BuiltinShader[];
}

export const NO_BUILTIN_EFFECTS: BuiltinEffectsShown = Object.freeze({
  approximate: Object.freeze([]),
  notPreviewed: Object.freeze([]),
});

/** Built-in effects drawn in this frame, split by fidelity, each name once, sorted. */
export function builtinEffectsIn(frame: Pick<FrameDrawList, 'effects' | 'globalEffects'>): BuiltinEffectsShown {
  const approximate = new Set<BuiltinShader>();
  const notPreviewed = new Set<BuiltinShader>();
  for (const e of [...frame.effects, ...frame.globalEffects]) {
    if (e.shader.kind !== 'builtin') continue;
    const f = builtinShaderFidelity(e.shader.name);
    // CR-W3-04-2: a signed-off ('verified') built-in gets no badge at all.
    if (f === 'approximate') approximate.add(e.shader.name);
    else if (f === 'notPreviewed') notPreviewed.add(e.shader.name);
  }
  if (approximate.size === 0 && notPreviewed.size === 0) return NO_BUILTIN_EFFECTS;
  return { approximate: [...approximate].sort(), notPreviewed: [...notPreviewed].sort() };
}

/** Same names (the stage state only changes when the badge would). */
export function sameBuiltinEffects(a: BuiltinEffectsShown, b: BuiltinEffectsShown): boolean {
  const eq = (x: readonly string[], y: readonly string[]): boolean =>
    x.length === y.length && x.every((v, i) => v === y[i]);
  return eq(a.approximate, b.approximate) && eq(a.notPreviewed, b.notPreviewed);
}
